import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { RefreshCw, X, Sparkles, AlertTriangle } from "lucide-react";

export function UpdateNotification() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [updateDetails, setUpdateDetails] = useState(null);
  const [isForceUpdate, setIsForceUpdate] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const handleUpdate = useCallback(() => {
    // حفظ الإصدار قبل أي شيء (في localStorage + cookie كنسخة احتياطية)
    if (updateDetails?.version) {
      try { localStorage.setItem('app_version', updateDetails.version); } catch (e) {}
      try {
        document.cookie = 'app_version=' + encodeURIComponent(updateDetails.version) + 
          '; path=/; max-age=' + (60 * 60 * 24 * 365) + '; SameSite=Lax';
      } catch (e) {}
    }
    
    // مسح الكاش (asynchronous - لا ننتظر)
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      }).catch(() => {});
    }
    
    // إخبار Service Worker بالتحديث (بدون unregister - آمن)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.update().catch(() => {});
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      }).catch(() => {});
    }
    
    // إعادة تحميل صارمة بعد إعطاء وقت للحفظ
    setTimeout(() => {
      window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
    }, 800);
  }, [updateDetails]);

  useEffect(() => {
    // التحقق الفوري من التحديثات عند تحميل الصفحة
    checkForUpdates();

    // الاستماع لرسائل Service Worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('[Update] SW Updated to version:', event.data.version);
          checkForUpdates();
        }
        if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
          setUpdateDetails(event.data.details || {});
          setShowUpdate(true);
        }
      });

      // التحقق من وجود Service Worker جديد
      navigator.serviceWorker.ready.then((registration) => {
        // فحص التحديثات فوراً
        registration.update();
        
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[Update] New Service Worker installed');
                checkForUpdates();
              }
            });
          }
        });
      });
    }

    // التحقق الدوري من التحديثات كل 2 دقيقة
    const interval = setInterval(checkForUpdates, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // عداد تنازلي للتحديث الإجباري
  useEffect(() => {
    if (isForceUpdate && showUpdate && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (isForceUpdate && countdown === 0) {
      handleUpdate();
    }
  }, [isForceUpdate, showUpdate, countdown, handleUpdate]);

  const checkForUpdates = async () => {
    try {
      // إضافة timestamp لتجنب الكاش
      const response = await fetch('/version.json?t=' + Date.now(), { 
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // قراءة الإصدار من localStorage أو cookie كـ fallback
        let currentVersion = null;
        try { currentVersion = localStorage.getItem('app_version'); } catch (e) {}
        if (!currentVersion) {
          // محاولة استخراج من cookie
          try {
            const match = document.cookie.match(/app_version=([^;]+)/);
            if (match) currentVersion = decodeURIComponent(match[1]);
          } catch (e) {}
          // إذا وُجد في cookie، أعد حفظه في localStorage
          if (currentVersion) {
            try { localStorage.setItem('app_version', currentVersion); } catch (e) {}
          }
        }
        
        console.log('[Update] Current:', currentVersion, 'Server:', data.version);
        
        // إذا كان هناك إصدار جديد
        if (!currentVersion || currentVersion !== data.version) {
          // ✨ احترام تأجيل المستخدم: لو ضغط "لاحقاً" واخترنا تذكيره خلال 6 ساعات
          // نعرض البانر فقط إذا انقضت المدة، أو إذا تغير الإصدار (تحديث جديد آخر)
          if (!data.forceUpdate) {
            try {
              const remindAt = parseInt(localStorage.getItem('update_remind_at') || '0');
              const dismissedVersion = localStorage.getItem('update_dismissed_version');
              if (
                remindAt > Date.now() &&
                dismissedVersion === data.version
              ) {
                console.log('[Update] Dismissed until', new Date(remindAt).toLocaleString());
                return; // المستخدم أجّل هذا الإصدار، لا نزعجه
              }
            } catch (e) {}
          }

          setUpdateDetails({
            version: data.version,
            date: data.date,
            changes: data.changes || []
          });
          
          // تحديث إجباري؟
          if (data.forceUpdate) {
            setIsForceUpdate(true);
            setCountdown(10);
          }
          
          setShowUpdate(true);
          
          // حفظ الإصدار الجديد دائماً (يحل مشكلة loop عند مستخدم جديد)
          // المستخدم سيرى modal مرة واحدة فقط، حتى لو forceUpdate=true
          if (!currentVersion) {
            try { localStorage.setItem('app_version', data.version); } catch (e) {}
            try {
              document.cookie = 'app_version=' + encodeURIComponent(data.version) + 
                '; path=/; max-age=' + (60 * 60 * 24 * 365) + '; SameSite=Lax';
            } catch (e) {}
          }
        }
      }
    } catch (error) {
      console.log('[Update] Check failed:', error);
    }
  };

  const handleDismiss = () => {
    // لا يمكن تجاهل التحديث الإجباري
    if (isForceUpdate) return;
    
    setShowUpdate(false);
    // ✨ تأجيل التذكير لمدة 6 ساعات (المستخدم اختار "لاحقاً")
    try {
      const remindAt = Date.now() + (6 * 60 * 60 * 1000); // 6 ساعات
      localStorage.setItem('update_remind_at', String(remindAt));
      // نحفظ الإصدار اللي تم تأجيله علشان لا نخفي بانر إصدار آخر جاي
      if (updateDetails?.version) {
        localStorage.setItem('update_dismissed_version', updateDetails.version);
      }
    } catch (e) {}
  };

  const [bannerExpanded, setBannerExpanded] = useState(false);

  if (!showUpdate) return null;

  // ✨ التحديث الاختياري: شريط علوي خفيف بدل مودال كامل
  if (!isForceUpdate) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] animate-slide-down">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 shadow-lg">
          {/* الشريط الرئيسي (دائماً ظاهر) */}
          <div className="max-w-screen-xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
            <button
              onClick={() => setBannerExpanded(!bannerExpanded)}
              className="flex items-center gap-2 text-white text-sm flex-1 min-w-0 hover:opacity-90"
              data-testid="update-banner-toggle"
            >
              <Sparkles className="w-4 h-4 flex-shrink-0 animate-pulse" />
              <span className="font-bold truncate">
                ترا اكو تحديثات جديدة! 🎉
              </span>
              <span className="text-white/80 text-xs hidden sm:inline">
                (الإصدار {updateDetails?.version})
              </span>
              <span className="text-white/70 text-xs">
                {bannerExpanded ? '▲' : '▼'}
              </span>
            </button>

            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                onClick={handleUpdate}
                size="sm"
                className="bg-white text-emerald-600 hover:bg-emerald-50 font-bold h-7 px-3 text-xs"
                data-testid="update-now-btn"
              >
                <RefreshCw className="w-3 h-3 me-1" />
                تحديث الآن
              </Button>
              <button
                onClick={handleDismiss}
                className="text-white/80 hover:text-white p-1"
                title="إغلاق - يمكنك التحديث لاحقاً"
                data-testid="update-dismiss-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* قائمة التحديثات (تظهر عند الضغط على السهم) */}
          {bannerExpanded && updateDetails?.changes && updateDetails.changes.length > 0 && (
            <div className="bg-emerald-700/50 border-t border-white/10 max-h-48 overflow-y-auto">
              <div className="max-w-screen-xl mx-auto px-4 py-3">
                <p className="text-white/90 text-xs font-semibold mb-2">ما الجديد:</p>
                <ul className="text-white text-sm space-y-1">
                  {updateDetails.changes.map((change, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-white/60 mt-0.5">•</span>
                      <span>{change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 🚨 التحديث الإجباري: مودال كامل كما كان
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md animate-slide-up">
        <div className={`rounded-xl shadow-2xl overflow-hidden ${
          isForceUpdate 
            ? 'bg-gradient-to-r from-amber-600 to-orange-600' 
            : 'bg-gradient-to-r from-emerald-600 to-teal-600'
        }`}>
          {/* Header */}
          <div className="p-4 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                {isForceUpdate ? (
                  <AlertTriangle className="w-6 h-6 text-white" />
                ) : (
                  <Sparkles className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">
                  {isForceUpdate ? 'تحديث مهم مطلوب!' : 'تحديث جديد متاح! 🎉'}
                </h3>
                <p className="text-white/80 text-sm">
                  {updateDetails?.version ? `الإصدار ${updateDetails.version}` : 'تحسينات وميزات جديدة'}
                </p>
              </div>
            </div>
            {!isForceUpdate && (
              <button 
                onClick={handleDismiss}
                className="text-white/70 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Changes List */}
          {updateDetails?.changes && updateDetails.changes.length > 0 && (
            <div className="px-4 pb-2">
              <div className="bg-white/10 rounded-lg p-3 max-h-32 overflow-y-auto">
                <p className="text-white/80 text-xs mb-2 font-semibold">ما الجديد:</p>
                <ul className="text-white text-sm space-y-1">
                  {updateDetails.changes.slice(0, 5).map((change, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-white/60 mt-1">•</span>
                      <span>{change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Force Update Warning */}
          {isForceUpdate && (
            <div className="px-4 pb-2">
              <div className="bg-red-500/30 rounded-lg p-3 text-center">
                <p className="text-white font-bold">
                  سيتم التحديث تلقائياً خلال {countdown} ثانية
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="p-4 pt-2 flex gap-2">
            <Button
              onClick={handleUpdate}
              className={`flex-1 font-bold ${
                isForceUpdate 
                  ? 'bg-white text-orange-600 hover:bg-orange-50' 
                  : 'bg-white text-emerald-600 hover:bg-emerald-50'
              }`}
              data-testid="update-now-btn"
            >
              <RefreshCw className="w-4 h-4 me-2" />
              تحديث الآن
            </Button>
            {!isForceUpdate && (
              <Button
                onClick={handleDismiss}
                variant="ghost"
                className="text-white hover:bg-white/20"
              >
                لاحقاً
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// إضافة CSS للأنيميشن
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-up {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes slide-down {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
  .animate-slide-down {
    animation: slide-down 0.4s ease-out;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}
